import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTimes, faHeart, faShare, faLink, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faInstagram, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import Modal from './Modal';
import AuthModal from './AuthModal';
import { AuthContext } from '../contexts/AuthContext';
import { 
  getPOIById, 
  getUserTrips, 
  getUserFavorites
} from '../services/api';
import cacheService from '../services/cacheService';
import TripCreationModal from './TripCreationModal';
import { searchHotels, getHotelDetails, searchCars } from '../services/amadeusService';
import HotelBookingForm from './HotelBookingForm';
import { format } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';


const SidePanelContainer = styled.div`
  width: 405px; /* Reduced by 10% from 450px */
  min-height: 320px;
  max-height: ${props => props.expanded ? 'calc(100vh - 80px)' : '660px'}; /* Increased by 20% from 550px */
  height: auto;
  background-color: white;
  padding: 0;
  overflow-y: auto;
  position: fixed;
  left: 20px;
  top: 80px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.isVisible ? '0' : '-120%'});
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1500;
  border-radius: 12px;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  
  @media (min-width: 1400px) {
    width: 27%; /* Reduced by 10% from 30% */
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-height: 70vh;
    min-height: 200px;
    height: auto;
    left: 0;
    top: auto;
    bottom: 0;
    border-radius: 20px 20px 0 0;
    transform: translateY(${props => props.isVisible ? '0' : '100%'});
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 768px) {
    // Mobile styles are now the same as desktop
    // We can remove these styles since they're identical
  }
`;

const ImageSection = styled.div`
  width: 100%;
  height: 240px; /* Increased by 20% from 200px */
  overflow: hidden;
  position: relative;
  border-radius: 12px 12px 0 0;

  @media (max-width: 768px) {
    border-radius: 20px 20px 0 0;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.5s ease;
`;

const Image = styled.div`
  flex: 0 0 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  cursor: pointer;
`;

const ImageNavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  cursor: pointer;
  z-index: 2;
  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;
  font-size: 1.2em;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const POIName = styled.h2`
  color: #0A2429;
  text-align: left;
  margin: 0;
  font: normal normal bold 30px/40px Poppins;
  letter-spacing: 0px;
  font-style: normal;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const POIDescriptionContainer = styled.div`
  margin: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  padding-bottom: 10px;
`;

const POIDescription = styled.p`
  color: #0A2429;
  text-align: left;
  font: normal normal medium 16px/25px Poppins;
  letter-spacing: 0px;
  margin: 0;
  text-justify: inter-word;
  hyphens: auto;
  position: relative;
  margin-bottom: 10px;
  white-space: pre-line;
  overflow: hidden;
  transition: max-height 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  max-height: ${props => props.expanded ? '500px' : '75px'};
  
  ${props => !props.expanded && `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `}
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 14px;
  font-weight: 500;
  padding: 5px 0;
  cursor: pointer;
  display: block;
  margin-top: 5px;
  margin-left: 0;
  text-align: left;
  
  &:hover {
    text-decoration: underline;
  }
`;



const FreeLabel = styled.span`
  color: #4CAF50;
  font-size: 16px;
  font-weight: 600;
`;

const PriceLabel = styled.span`
  color: #4CAF50;
  font-size: 16px;
  font-weight: 600;
`;

const SectionCard = styled.div`
  background-color: white;
  margin-bottom: 20px;
  padding: 10px 20px;
  opacity: 0;
  transform: translateY(20px);
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionHeader = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
  }
`;

const PlacesSection = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding-bottom: 5px; // Add padding for scrollbar
  margin-top: 15px;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.5);
    border-radius: 20px;
  }
`;

const PlaceCard = styled.div`
  flex: 0 0 190px; // Fixed width for cards
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }
`;

const PlaceImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
`;

const PlaceInfo = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  position: relative;
  background-color: #F6F7FB;
`;

const PlaceTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  text-align: left;
  color: #333;
`;

const AddToTripButton = styled.button`
  padding: 10px 20px;
  background-color: #F38530;
  color: #FFFFFF;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 80%;
  text-align: center;
  text-transform: uppercase;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0px;  
  &:hover {
    background-color: #d67428;
  }
`;

// First, define the base dropdown items
const DropdownItem = styled.div`
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const FixedDropdownItem = styled.div`
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

// Then define the menu and specialized items
const DropdownMenu = styled.div`
  position: absolute;
  bottom: 60px;
  right: 20px;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 1002;
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
`;

const NewTripOption = styled(FixedDropdownItem)`
  background-color: #f8f8f8;
  border-bottom: 2px solid #e0e0e0;
  color: #316C6E;
  font-weight: 500;
  padding: 12px 15px;
  
  &:hover {
    background-color: #eef8f8;
  }
`;

const TripOption = styled(DropdownItem)`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  
  span.trip-name {
    font-weight: 500;
  }
  
  span.trip-dates {
    font-size: 12px;
    color: #666;
  }
`;


const BookButton = styled.div`
  padding: 10px;
  color: #F38530;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background-color: #F6F7FB;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;


const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #14464F;
  font-size: 16px;
  flex-shrink: 0;
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 14px;
  cursor: pointer;
  padding: 10px 0;
  text-decoration: underline;
  
  &:hover {
    color: #254F51;
  }
`;

const Address = styled.div`
  font-size: 12px;
  color: #666;
`;

// Add this styled component with the others at the top
const ImagePlaceholder = styled.div`
  width: 100%;
  height: 120px;
  background-color: #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
  font-size: 14px;
`;

// Add this helper function at the top of the file
const transformHotelData = (hotel) => ({
  id: hotel.hotelId,
  name: hotel.name,
  description: 'Hotel near point of interest',
  image_url: hotel.media?.[0]?.uri || 'https://via.placeholder.com/300x200?text=Hotel+Image',
  type: 'hotel',
  lat: parseFloat(hotel.latitude),
  lng: parseFloat(hotel.longitude),
  hotelId: hotel.hotelId, // Keep the original hotelId for Amadeus API calls
  isAmadeusHotel: true
});

// Add these new styled components
const HotelDetailsSection = styled.div`
  padding: 20px;
`;

const HotelDetail = styled.div`
  margin-bottom: 20px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const DetailTitle = styled.h4`
  color: #333;
  margin: 0;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const DetailContent = styled.div`
  color: #666;
  font-size: 14px;
`;

const AmenityList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  margin-top: 10px;
  overflow: hidden;
  position: relative;
  height: ${props => props.expanded ? 'auto' : '32px'};
  flex-wrap: ${props => props.expanded ? 'wrap' : 'nowrap'};
`;

const AmenityTag = styled.span`
  background-color: #f0f0f0;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ExpandAmenitiesButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 12px;
  cursor: pointer;
  padding: 5px 10px;
  margin-left: 5px;
  position: absolute;
  right: 0;
  top: 0;
  height: 32px;
  background: linear-gradient(90deg, transparent, #fff 30%);
  padding-left: 30px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AddressLine = styled.p`
  margin: 5px 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const RoomsSection = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding-bottom: 5px;
  margin-top: 15px;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.5);
    border-radius: 20px;
  }
`;

const RoomCard = styled.div`
  flex: 0 0 200px; // Fixed width at 200px (half of previous size)
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background-color: #f8f8f8;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }
`;

const RoomTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
`;

const RoomDetail = styled.p`
  margin: 5px 0;
  color: #666;
  font-size: 14px;
`;

const RoomPrice = styled.div`
  font-size: 18px;
  color: #316C6E;
  font-weight: bold;
  margin-top: 10px;
`;

const PricePerNight = styled.span`
  font-size: 12px;
  font-weight: normal;
`;

const BookNowButton = styled.button`
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  margin-top: 10px;
  font-size: 14px;
  width: 100%;

  &:hover {
    background-color: #254F51;
  }
`;

const BackToPoiButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 10;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5); // Add shadow to make text visible over any image
`;

// Add these styled components for skeleton loading
const SkeletonPulse = styled.div`
  animation: pulse 1.5s ease-in-out infinite;
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 400% 400%;
  
  @keyframes pulse {
    0% { background-position: 0% 0%; }
    100% { background-position: -135% 0%; }
  }
`;

const SkeletonImage = styled(SkeletonPulse)`
  width: 100%;
  height: 240px; /* Increased by 20% from 200px */
`;

const SkeletonLine = styled(SkeletonPulse)`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  margin-bottom: ${props => props.mb || '10px'};
  border-radius: 4px;
`;

const SkeletonAmenityTag = styled(SkeletonPulse)`
  width: 100px;
  height: 24px;
  border-radius: 12px;
  margin: 5px;
`;

const SkeletonAmenityGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 0;
`;

const SkeletonRoomCard = styled(SkeletonPulse)`
  width: 100%;
  height: 150px;
  border-radius: 8px;
  margin-bottom: 15px;
`;


// Add these styled components for the action buttons
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

// First, define SharePopup
const SharePopup = styled.div`
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  margin-right: 10px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 10px;
  display: flex;
  gap: 10px;
  z-index: 1100;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-right: 10px;
`;

// Then define ActionButton
const ActionButton = styled.button`
  background-color: #F6F7FB;
  border: none;
  color: ${props => props.active ? '#14464F' : '#8B9DA0'};
  cursor: pointer;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  background-color: ${props => props.active ? '#F6F7FB' : '#F6F7FB'};

  &:hover {
    background-color: ${props => props.active ? '#e8e9ed' : '#e8e9ed'};
  }
`;

// Finally define ShareButton that uses both SharePopup and ActionButton
const ShareButton = styled(ActionButton)`
  position: relative;
  color: #14464F;
  background-color: #F6F7FB;

  &:hover {
    background-color: #e8e9ed;
  }

  &:hover ${SharePopup} {
    opacity: 1;
    visibility: visible;
  }
`;

// Update the POIName container to include the action buttons
const POINameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 15px 20px 10px 20px;
  gap: 10px;
  flex-wrap: nowrap;
`;

const SocialIcon = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 1.2rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &.whatsapp {
    background-color: #25D366;
  }

  &.twitter {
    background-color: #000000;
  }

  &.email {
    background-color: #4285F4;
  }

  &.copy {
    background-color: #6c757d;
  }
`;


// Add these new styled components near the other skeleton components
const SkeletonHotelCard = styled.div`
  flex: 0 0 200px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SkeletonHotelImage = styled(SkeletonPulse)`
  width: 100%;
  height: 120px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px 20px 20px;
  background: white;
  margin-top: 10px;
`;

const CloseIconButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1100;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const ModalNavigationButton = styled(ImageNavigationButton)`
  z-index: 1200;
  padding: 15px;
  font-size: 1.2em;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const ModalImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
`;

const ModalImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  opacity: 0;
  transform: scale(0.98);
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

  &.loaded {
    opacity: 1;
    transform: scale(1);
  }
`;

// Update the calculateTextHeight helper function
const calculateTextHeight = (text, fontSize = 16, containerWidth) => {
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `
    font: normal normal medium ${fontSize}px/25px Poppins;
    width: ${containerWidth}px;
    position: absolute;
    visibility: hidden;
    white-space: normal;
    padding: 0 20px;
  `;
  tempDiv.innerText = text;
  document.body.appendChild(tempDiv);
  const height = tempDiv.offsetHeight;
  document.body.removeChild(tempDiv);
  return height;
};

const POIDetailsSidePanel = ({ poi, onClose, onShowToast }) => {
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [placesToStayOpen, setPlacesToStayOpen] = useState(false);
  const [placesToEatOpen, setPlacesToEatOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [autoMove, setAutoMove] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userTrips, setUserTrips] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isAuthenticated } = React.useContext(AuthContext);
  const [placesToStay, setPlacesToStay] = useState([]);
  const [placesToEat, setPlacesToEat] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [toastLink, setToastLink] = useState(null);
  const [showTripCreationModal, setShowTripCreationModal] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [isAdded, setIsAdded] = useState(false);
  const [currentPOI, setCurrentPOI] = useState(poi);
  const [previousPOI, setPreviousPOI] = useState(null);
  const [nearbyHotels, setNearbyHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const dropdownRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const images = currentPOI.isAmadeusHotel 
    ? currentPOI.media?.map(m => m.uri) || []
    : (currentPOI.image_url 
        ? (typeof currentPOI.image_url === 'string' && currentPOI.image_url.startsWith('[') 
            ? JSON.parse(currentPOI.image_url) 
            : [currentPOI.image_url]) 
        : []);

  useEffect(() => {
    let timer;
    const currentImages = currentPOI.image_url ? 
      (typeof currentPOI.image_url === 'string' && currentPOI.image_url.startsWith('[') 
        ? JSON.parse(currentPOI.image_url) 
        : [currentPOI.image_url]) 
      : [];

    // Only start auto-rotation if there are multiple images
    if (autoMove && currentImages.length > 1) {
      timer = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % currentImages.length);
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [currentPOI.image_url, autoMove]);

  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        await fetchUserTrips();
      } finally {
        // Don't set loading to false here, let loadNearbyHotels handle it
        if (!currentPOI.lat || !currentPOI.lng) {
          setIsLoading(false);
        }
      }
    };
    loadData();
  }, [poi, isAuthenticated, currentPOI.lat, currentPOI.lng]);

  // Restore the POI initialization effect
  useEffect(() => {
    setCurrentPOI(poi);
    setPreviousPOI(null);
    setCurrentImageIndex(0);
    setAutoMove(true);
  }, [poi]);

  useEffect(() => {
    const loadNearbyHotels = async () => {
      if (!currentPOI.lat || !currentPOI.lng || !isAuthenticated) {
        setNearbyHotels([]);
        setIsLoading(false);
        return;
      }

      try {
        setNearbyHotels([]);
      } catch (error) {
        console.error('Error loading nearby hotels:', error);
        setNearbyHotels([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadNearbyHotels();
  }, [currentPOI.lat, currentPOI.lng, isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (isAuthenticated) {
        try {
          const favorites = await getUserFavorites();
          setIsFavorite(favorites.some(fav => fav.id === currentPOI.id));
        } catch (error) {
          console.error('Error fetching favorites:', error);
        }
      } else {
        const localFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        setIsFavorite(localFavorites.includes(currentPOI.id));
      }
    };

    checkFavoriteStatus();
  }, [isAuthenticated, currentPOI.id]);

  const fetchUserTrips = async () => {
    try {
      const trips = await getUserTrips();
      setUserTrips(trips);
    } catch (error) {
      console.error('Error fetching user trips:', error);
    }
  };

  const handleImageClick = (image, index) => {
    setImageLoaded(false);
    setModalImage(image);
    setModalImageIndex(index);
  };

  const handleModalPrevImage = () => {
    setImageLoaded(false);
    setTimeout(() => {
      const newIndex = (modalImageIndex - 1 + images.length) % images.length;
      setModalImageIndex(newIndex);
      setModalImage(images[newIndex]);
    }, 150); // Half of the transition duration to create a cross-fade effect
  };

  const handleModalNextImage = () => {
    setImageLoaded(false);
    setTimeout(() => {
      const newIndex = (modalImageIndex + 1) % images.length;
      setModalImageIndex(newIndex);
      setModalImage(images[newIndex]);
    }, 150); // Half of the transition duration to create a cross-fade effect
  };

  const handleAddToTrip = () => {
    if (!isAuthenticated) {
      const savedPOIs = JSON.parse(localStorage.getItem('savedPOIs')) || [];
      if (!savedPOIs.includes(currentPOI.id)) {
        savedPOIs.push(currentPOI.id);
        localStorage.setItem('savedPOIs', JSON.stringify(savedPOIs));
        setToastMessage('POI added to trip in draft. Start planning your trip now!');
        setToastType('success');
        setShowToast(true);
      }
      setSelectedPlaceId(currentPOI.id);
      setShowAuthModal(true);
      return;
    }

    if (userTrips.length === 0) {
      fetchUserTrips();
    }
    setSelectedPlaceId(currentPOI.id);
    setShowDropdown(true);
  };

  const handleTripSelect = async (tripId) => {
    try {
      await cacheService.addPOIToTrip(tripId, selectedPlaceId);
      setShowDropdown(false);
      setIsAdded(true);
      
      const link = {
        url: `/trip/${tripId}`,
        text: '[View Trip]'
      };
      
      onShowToast('POI added to trip successfully!', 'success', link);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        onShowToast('This POI is already in the selected trip.', 'error');
      } else {
        onShowToast('Error adding POI to trip. Please try again.', 'error');
      }
    }
  };

  const handleCreateTrip = async (tripData) => {
    try {
      const newTrip = await cacheService.createTrip({
        name: `Trip to ${currentPOI.name}`, // Add a default name based on the POI
        ...tripData,
        pois: [selectedPlaceId]
      });
      setShowTripCreationModal(false);
      
      const link = {
        url: `/trip/${newTrip.id}`,
        text: '[View Trip]'
      };
      
      onShowToast('Trip created successfully!', 'success', link);
      setIsAdded(true);
    } catch (error) {
      console.error('Error creating trip:', error);
      onShowToast('Failed to create trip. Please try again.', 'error');
    }
  };

  const handleCardClick = async (place, isAmadeusHotel) => {
    if (isAmadeusHotel) {
      try {
        const hotelDetails = await getHotelDetails(place.hotelId);
        const transformedHotel = {
          ...hotelDetails.data,
          isAmadeusHotel: true,
          image_url: hotelDetails.data.media.map(m => m.uri)
        };
        setPreviousPOI(currentPOI);
        setCurrentPOI(transformedHotel);
        setCurrentImageIndex(0); // Reset image index when loading new hotel
      } catch (error) {
        console.error('Error fetching hotel details:', error);
      }
    } else {
      setPreviousPOI(currentPOI);
      setCurrentPOI(place);
    }
  };

  const handleBack = () => {
    if (previousPOI) {
      setCurrentPOI(previousPOI);
      setPreviousPOI(null);
    }
  };

  const renderPlaces = (places, title) => {
    if (title === "Recommended Hotels") {
      places = nearbyHotels.slice(0, 10);
      if (places.length === 0 && !isLoading) return null;

      return (
        <SectionCard>
          <SectionHeader>
            <h3>{translations[language].poi.recommendedHotels}</h3>
          </SectionHeader>
          <PlacesSection>
            {isLoading ? (
              // Render skeleton cards while loading
              [...Array(4)].map((_, index) => (
                <SkeletonHotelCard key={index}>
                  <SkeletonHotelImage />
                  <div style={{ padding: '10px' }}>
                    <SkeletonLine height="16px" width="70%" mb="10px" />
                    <SkeletonLine height="12px" width="90%" mb="8px" />
                    <SkeletonLine height="12px" width="60%" mb="8px" />
                  </div>
                  <SkeletonLine height="32px" width="100%" mb="0" />
                </SkeletonHotelCard>
              ))
            ) : places.length > 0 ? (
              places.map((place, index) => {
                const isAmadeusHotel = !!place.hotelId;
                
                const name = isAmadeusHotel ? 
                  (place.name?.content || place.name || 'Unnamed Hotel') : 
                  place.name;

                const imageUrl = isAmadeusHotel ? 
                  (place.media?.[0]?.uri || null) : 
                  (place.image_url || null);
                
                const address = isAmadeusHotel && place.address && 
                  Array.isArray(place.address.lines) && 
                  place.address.lines.length > 0 ? 
                  place.address.lines.join(', ') : null;
                
                return (
                  <PlaceCard 
                    key={index} 
                    onClick={() => handleCardClick(place, isAmadeusHotel)}
                  >
                    {imageUrl ? (
                      <PlaceImage 
                        src={imageUrl} 
                        alt={name}
                        onError={(e) => {
                          e.target.parentNode.replaceChild(
                            Object.assign(document.createElement('div'), {
                              className: e.target.className,
                              style: 'background-color: #e0e0e0; display: flex; justify-content: center; align-items: center; color: #666;',
                              textContent: 'Image not available'
                            }),
                            e.target
                          );
                        }}
                      />
                    ) : (
                      <ImagePlaceholder>Image not available</ImagePlaceholder>
                    )}
                    <PlaceInfo>
                      <PlaceTitle>{name}</PlaceTitle>
                      {isAmadeusHotel && (
                        <>
                          {place.rating && <StarRating>{place.rating}</StarRating>}
                          {address && <Address>{address}</Address>}
                        </>
                      )}
                    </PlaceInfo>
                    <BookButton>
                      {translations[language].poi.viewDetails} <FontAwesomeIcon icon={faChevronRight} />
                    </BookButton>
                  </PlaceCard>
                );
              })
            ) : (
              <p>No hotels found in this area.</p>
            )}
          </PlacesSection>
        </SectionCard>
      );
    }

    if (!places || places.length === 0) return null;

    return (
      <SectionCard>
        <SectionHeader>
          <h3>{title}</h3>
        </SectionHeader>
        <PlacesSection>
          {places.map((place, index) => {
            const isAmadeusHotel = !!place.hotelId;
            
            const name = isAmadeusHotel ? 
              (place.name?.content || place.name || 'Unnamed Hotel') : 
              place.name;

            const imageUrl = isAmadeusHotel ? 
              (place.media?.[0]?.uri || null) : 
              (place.image_url || null);
            
            const address = isAmadeusHotel && place.address && 
              Array.isArray(place.address.lines) && 
              place.address.lines.length > 0 ? 
              place.address.lines.join(', ') : null;
            
            return (
              <PlaceCard 
                key={index} 
                onClick={() => handleCardClick(place, isAmadeusHotel)}
              >
                {imageUrl ? (
                  <PlaceImage 
                    src={imageUrl} 
                    alt={name}
                    onError={(e) => {
                      e.target.parentNode.replaceChild(
                        Object.assign(document.createElement('div'), {
                          className: e.target.className,
                          style: 'background-color: #e0e0e0; display: flex; justify-content: center; align-items: center; color: #666;',
                          textContent: 'Image not available'
                        }),
                        e.target
                      );
                    }}
                  />
                ) : (
                  <ImagePlaceholder>Image not available</ImagePlaceholder>
                )}
                <PlaceInfo>
                  <PlaceTitle>{name}</PlaceTitle>
                  {isAmadeusHotel && (
                    <>
                      {place.rating && <StarRating>{place.rating}</StarRating>}
                      {address && <Address>{address}</Address>}
                    </>
                  )}
                </PlaceInfo>
                <BookButton>
                  View Details <FontAwesomeIcon icon={faChevronRight} />
                </BookButton>
              </PlaceCard>
            );
          })}
        </PlacesSection>
      </SectionCard>
    );
  };

  const handleBookNow = (room) => {
    setSelectedRoom({
      ...room,
      hotelName: currentPOI.name,
      hotelLat: currentPOI.location?.latitude || currentPOI.lat,
      hotelLng: currentPOI.location?.longitude || currentPOI.lng
    });
    setShowBookingForm(true);
    setBookingStep(1);
  };

  const handleBookingNext = () => {
    setBookingStep(2);
  };

  const handleBackToForm = () => {
    setBookingStep(1);
  };

  const handleBackToHotel = () => {
    setShowBookingForm(false);
    setSelectedRoom(null);
    setBookingStep(1);
  };

  const handleBookingComplete = ({ tripType, tripId, withBooking }) => {
    if (withBooking) {
      // Close the side panel after the toast is shown
      setTimeout(() => {
        onClose();
      }, 2000);
    }
    setShowBookingForm(false);
    setToastMessage('Booking completed successfully!');
    setToastType('success');
    if (tripId) {
      setToastLink({
        url: `/trip/${tripId}`,
        text: 'View Trip'
      });
    }
    setShowToast(true);
  };

  // Update the getSortedTrips function
  const getSortedTrips = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return userTrips
      .filter(trip => {
        const endDate = new Date(trip.end_date);
        endDate.setHours(0, 0, 0, 0);
        return endDate >= today;
      })
      .sort((a, b) => {
        // Sort by updated_at in descending order (newest first)
        return new Date(b.updated_at) - new Date(a.updated_at);
      })
      .reverse(); // Add this to reverse the final order
  };

  const renderSkeleton = () => (
    <SidePanelContainer>
      <SkeletonImage />
      
      <div style={{ padding: '20px' }}>
        <SkeletonLine width="70%" height="24px" mb="15px" />
        <SkeletonLine width="90%" height="16px" mb="10px" />
        <SkeletonLine width="85%" height="16px" mb="20px" />
        
        {/* Amenities Section */}
        <SkeletonLine width="150px" height="20px" mb="15px" />
        <SkeletonAmenityGrid>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <SkeletonAmenityTag key={i} />
          ))}
        </SkeletonAmenityGrid>
        
        {/* Rooms Section */}
        <SkeletonLine width="150px" height="20px" mb="15px" />
        {[1, 2].map(i => (
          <SkeletonRoomCard key={i} />
        ))}
        
        {/* Additional Details */}
        <SkeletonLine width="150px" height="20px" mb="15px" />
        <SkeletonLine width="100%" height="16px" mb="10px" />
        <SkeletonLine width="95%" height="16px" mb="10px" />
        <SkeletonLine width="90%" height="16px" mb="20px" />
      </div>
    </SidePanelContainer>
  );

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (isFavorite) {
      try {
        await cacheService.removeFavorite(currentPOI.id);
        setIsFavorite(false);
        onShowToast('Removed from favorites', 'success');
      } catch (error) {
        console.error('Error removing favorite:', error);
        onShowToast('Failed to remove from favorites', 'error');
      }
    } else {
      try {
        await cacheService.addFavorite(currentPOI.id);
        onShowToast('Successfully added to favorites', 'success');
        setIsFavorite(true);
      } catch (error) {
        console.error('Error updating favorite:', error);
        onShowToast('Error updating favorite. Please try again.', 'error');
      }
    }
  };

  const handleSocialShare = (platform) => {
    const baseUrl = window.location.origin;
    const poiUrl = `${baseUrl}/poi/${currentPOI.id}`;
    const shareText = `I'm excited to explore ${currentPOI.name}!`;

    switch (platform) {
      case 'whatsapp':
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const whatsappUrl = isMobile
          ? `whatsapp://send?text=${encodeURIComponent(`${shareText}\n${poiUrl}`)}`
          : `https://web.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${poiUrl}`)}`;
        window.open(whatsappUrl, '_blank');
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(poiUrl)}&hashtags=Travel`,
          '_blank'
        );
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(`Check out ${currentPOI.name}!`)}&body=${encodeURIComponent(`${shareText}\n\n${poiUrl}`)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareText}\n${poiUrl}`).then(() => {
          onShowToast('Link copied to clipboard!', 'success');
        });
        break;
      default:
        break;
    }
  };

  // Add touch gesture handling
  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
    
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      touchEndY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = () => {
      const swipeDistance = touchEndY - touchStartY;
      if (swipeDistance > 100) { // Swipe down
        onClose();
      }
    };
    
    if (window.innerWidth <= 768) {
      const element = document.getElementById('side-panel');
      if (element) {
        element.addEventListener('touchstart', handleTouchStart);
        element.addEventListener('touchmove', handleTouchMove);
        element.addEventListener('touchend', handleTouchEnd);
      }
      
      return () => {
        if (element) {
          element.removeEventListener('touchstart', handleTouchStart);
          element.removeEventListener('touchmove', handleTouchMove);
          element.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, [onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match this with the transition duration
  };

  useEffect(() => {
    // Set a tiny delay to ensure the initial false state is rendered
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (currentPOI?.description) {
      const containerWidth = document.querySelector('.poi-description-container')?.offsetWidth || 400;
      const textHeight = calculateTextHeight(currentPOI.description, 16, containerWidth);
      const lineHeight = 25; // matches the line-height in the styled component
      const threeLineHeight = lineHeight * 3;
      setHasMoreContent(textHeight > threeLineHeight);
    }
  }, [currentPOI?.description]);

  const getLocalizedField = (field, spanishField) => {
    return language === 'es' ? spanishField : field;
  };

  return (
    <>
      {isLoading ? renderSkeleton() : (
        <>
          <SidePanelContainer isVisible={isVisible} expanded={descriptionExpanded}>
            <CloseButton onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            {showBookingForm ? (
              <>
                <HotelBookingForm 
                  room={selectedRoom}
                  poi={poi}
                  onBack={handleBackToHotel}
                  onComplete={handleBookingComplete}
                  onNext={handleBookingNext}
                  step={bookingStep}
                />
              </>
            ) : (
              <>
                {currentPOI.isAmadeusHotel && (
                  <BackToPoiButton onClick={() => handleBack()}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    {translations[language].poi.backToPoi} {poi.name}
                  </BackToPoiButton>
                )}
                <ImageSection>
                  <ImageWrapper style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}>
                    {images.map((image, index) => (
                      <Image 
                        key={index}
                        image={image}
                        onClick={() => handleImageClick(image, index)}
                      />
                    ))}
                  </ImageWrapper>
                  {images.length > 1 && (
                    <>
                      <ImageNavigationButton 
                        direction="left" 
                        onClick={() => {
                          setAutoMove(false);
                          setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </ImageNavigationButton>
                      <ImageNavigationButton 
                        direction="right" 
                        onClick={() => {
                          setAutoMove(false);
                          setCurrentImageIndex((prev) => (prev + 1) % images.length);
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </ImageNavigationButton>
                    </>
                  )}
                </ImageSection>
                {currentPOI.isAmadeusHotel ? (
                  <HotelDetailsSection>
                    <TitleContainer>
                      <DetailTitle>
                        {getLocalizedField(currentPOI.name, currentPOI.name_es)}
                      </DetailTitle>
                      {currentPOI.rating && (
                        <StarRating>
                          {[...Array(currentPOI.rating)].map((_, i) => (
                            <FontAwesomeIcon key={i} icon={faHeart} />
                          ))}
                        </StarRating>
                      )}
                    </TitleContainer>
                    
                    <POIDescriptionContainer>
                      <POIDescription>
                        {language === 'es' ? currentPOI?.description_es : currentPOI?.description}
                      </POIDescription>
                    </POIDescriptionContainer>

                    {currentPOI.amenities?.length > 0 && (
                      <HotelDetail>
                        <DetailTitle>{translations[language].poi.hotelDetails.amenities}</DetailTitle>
                        <div style={{ position: 'relative' }}>
                          {!amenitiesExpanded && (
                            <AmenityList>
                              {currentPOI.amenities.map((amenity, index) => (
                                <AmenityTag key={index}>
                                  {amenity.split('_').map(word => 
                                    word.charAt(0) + word.slice(1).toLowerCase()
                                  ).join(' ')}
                                </AmenityTag>
                              ))}
                            </AmenityList>
                          )}
                          {amenitiesExpanded && (
                            <AmenityList expanded>
                              {currentPOI.amenities.map((amenity, index) => (
                                <AmenityTag key={index}>
                                  {amenity.split('_').map(word => 
                                    word.charAt(0) + word.slice(1).toLowerCase()
                                  ).join(' ')}
                                </AmenityTag>
                              ))}
                            </AmenityList>
                          )}
                          {currentPOI.amenities.length > 3 && (
                            <ExpandAmenitiesButton onClick={() => setAmenitiesExpanded(!amenitiesExpanded)}>
                              {amenitiesExpanded ? 
                                translations[language].poi.rooms.showLessAmenities : 
                                translations[language].poi.rooms.showMoreAmenities}
                            </ExpandAmenitiesButton>
                          )}
                        </div>
                      </HotelDetail>
                    )}

                    {currentPOI.rooms && currentPOI.rooms.length > 0 && (
                      <HotelDetail>
                        <DetailTitle>{translations[language].poi.rooms.availableRooms}</DetailTitle>
                        <RoomsSection>
                          {currentPOI.rooms.map((room, index) => (
                            <RoomCard key={index}>
                              <RoomTitle>{room.category}</RoomTitle>
                              <RoomDetail>
                                {room.beds} {room.bedType.toLowerCase()} {' '}
                                {room.beds > 1 ? 
                                  translations[language].poi.rooms.beds_plural : 
                                  translations[language].poi.rooms.beds}
                              </RoomDetail>
                              {room.description && (
                                <RoomDetail>{room.description}</RoomDetail>
                              )}
                              <RoomDetail>
                                {translations[language].poi.rooms.maxGuests}: {room.guests.adults} {' '}
                                {translations[language].poi.rooms.adults}
                              </RoomDetail>
                              <RoomPrice>
                                {room.price.currency} {parseFloat(room.price.amount).toFixed(2)} {' '}
                                <PricePerNight>{translations[language].poi.rooms.perNight}</PricePerNight>
                              </RoomPrice>
                              <BookNowButton onClick={() => handleBookNow(room)}>
                                {translations[language].poi.bookNow}
                              </BookNowButton>
                            </RoomCard>
                          ))}
                        </RoomsSection>
                      </HotelDetail>
                    )}

                    <SeeMoreButton onClick={() => setShowMoreDetails(!showMoreDetails)}>
                      {showMoreDetails ? 
                        translations[language].poi.rooms.seeLessDetails : 
                        translations[language].poi.rooms.seeMoreDetails}
                    </SeeMoreButton>

                    {showMoreDetails && (
                      <>
                        {currentPOI.address && (
                          <HotelDetail>
                            <DetailTitle>{translations[language].poi.hotelDetails.address}</DetailTitle>
                            <DetailContent>
                              {currentPOI.address.lines?.map((line, index) => (
                                <AddressLine key={index}>{line}</AddressLine>
                              ))}
                              {currentPOI.address.cityName && (
                                <AddressLine>
                                  {currentPOI.address.cityName}
                                  {currentPOI.address.postalCode && `, ${currentPOI.address.postalCode}`}
                                </AddressLine>
                              )}
                              {currentPOI.address.countryCode && (
                                <AddressLine>{currentPOI.address.countryCode}</AddressLine>
                              )}
                            </DetailContent>
                          </HotelDetail>
                        )}

                        {(currentPOI.contact?.phone || currentPOI.contact?.email) && (
                          <HotelDetail>
                            <DetailTitle>{translations[language].poi.hotelDetails.contact}</DetailTitle>
                            <ContactInfo>
                              {currentPOI.contact.phone && (
                                <DetailContent>
                                  {translations[language].poi.hotelDetails.phone}: {currentPOI.contact.phone}
                                </DetailContent>
                              )}
                              {currentPOI.contact.email && (
                                <DetailContent>
                                  {translations[language].poi.hotelDetails.email}: {currentPOI.contact.email}
                                </DetailContent>
                              )}
                            </ContactInfo>
                          </HotelDetail>
                        )}
                      </>
                    )}
                  </HotelDetailsSection>
                ) : (
                  <>
                    <POINameContainer>
                      <POIName>
                        {getLocalizedField(currentPOI.name, currentPOI.name_es)}
                      </POIName>
                      {currentPOI.price ? (
                        <PriceLabel>RD$ {currentPOI.price}</PriceLabel>
                      ) : (
                        <FreeLabel>Free</FreeLabel>
                      )}
                    </POINameContainer>

                    <POIDescriptionContainer>
                      <POIDescription expanded={descriptionExpanded}>
                        {language === 'es' ? currentPOI?.description_es : currentPOI?.description}
                      </POIDescription>
                      {(() => {
                        const description = language === 'es' ? currentPOI?.description_es : currentPOI?.description;
                        if (!description) return null;
                        
                        // Check if description is long enough to need truncation
                        const words = description.split(' ');
                        const lines = description.split('\n');
                        const needsTruncation = words.length > 30 || lines.length > 3 || description.length > 200;
                        
                        return needsTruncation ? (
                          <ShowMoreButton onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
                            {descriptionExpanded ? 'See less' : 'See more'}
                          </ShowMoreButton>
                        ) : null;
                      })()}
                    </POIDescriptionContainer>

                    <ButtonsContainer>
                      <AddToTripButton onClick={handleAddToTrip}>
                        {translations[language].poi.addToTrip}
                      </AddToTripButton>
                      <ActionButtonsContainer>
                        <ActionButton
                          onClick={handleFavorite}
                          active={isFavorite}
                          data-tooltip={isFavorite ? 
                            translations[language].poi.removeFromFavorites : 
                            translations[language].poi.addToFavorites}
                        >
                          <FontAwesomeIcon icon={faHeart} />
                        </ActionButton>
                        <ShareButton>
                          <FontAwesomeIcon icon={faShare} />
                          <SharePopup>
                            <SocialIcon 
                              className="whatsapp" 
                              onClick={() => handleSocialShare('whatsapp')}
                              title="Share on WhatsApp"
                            >
                              <FontAwesomeIcon icon={faWhatsapp} />
                            </SocialIcon>
                            <SocialIcon 
                              className="twitter" 
                              onClick={() => handleSocialShare('twitter')}
                              title="Share on X"
                            >
                              <FontAwesomeIcon icon={faXTwitter} />
                            </SocialIcon>
                            <SocialIcon 
                              className="email" 
                              onClick={() => handleSocialShare('email')}
                              title="Share via Email"
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                            </SocialIcon>
                            <SocialIcon 
                              className="copy" 
                              onClick={() => handleSocialShare('copy')}
                              title="Copy link"
                            >
                              <FontAwesomeIcon icon={faLink} />
                            </SocialIcon>
                          </SharePopup>
                        </ShareButton>
                      </ActionButtonsContainer>
                    </ButtonsContainer>

                    {currentPOI.id === poi.id && renderPlaces(null, "Recommended Hotels")}
                  </>
                )}
              </>
            )}
            {showDropdown && (
              <DropdownMenu ref={dropdownRef}>
                <NewTripOption onClick={() => setShowTripCreationModal(true)}>
                  ＋ Create New Trip
                </NewTripOption>
                {getSortedTrips().map(trip => (
                  <TripOption key={trip.id} onClick={() => handleTripSelect(trip.id)}>
                    <span className="trip-name">{trip.name}</span>
                    <span className="trip-dates">
                      {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                    </span>
                  </TripOption>
                ))}
              </DropdownMenu>
            )}
          </SidePanelContainer>

          {modalImage && (
            <Modal 
              onClose={() => {
                setModalImage(null);
                setModalImageIndex(0);
                setImageLoaded(false);
              }}
              fullScreen
            >
              <CloseIconButton onClick={() => {
                setModalImage(null);
                setModalImageIndex(0);
                setImageLoaded(false);
              }}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseIconButton>
              <ModalImageContainer>
                <ModalImage 
                  src={modalImage} 
                  alt="Place" 
                  className={imageLoaded ? 'loaded' : ''}
                  onLoad={() => setImageLoaded(true)}
                />
                {images.length > 1 && (
                  <>
                    <ModalNavigationButton 
                      direction="left" 
                      onClick={handleModalPrevImage}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </ModalNavigationButton>
                    <ModalNavigationButton 
                      direction="right" 
                      onClick={handleModalNextImage}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </ModalNavigationButton>
                  </>
                )}
              </ModalImageContainer>
            </Modal>
          )}
          
          {showAuthModal && (
            <AuthModal
              onClose={() => setShowAuthModal(false)}
              onAuthSuccess={(user, token) => {
                setShowAuthModal(false);
                fetchUserTrips().then(() => {
                  setShowDropdown(true); // Automatically show dropdown after auth
                });
              }}
            />
          )}
          
          {showTripCreationModal && (
            <TripCreationModal
              onClose={() => setShowTripCreationModal(false)}
              onCreateTrip={handleCreateTrip}
              initialPOI={poi}
            />
          )}
        </>
      )}
    </>
  );
};

export default POIDetailsSidePanel;
